import { Component } from "react";
import { RefreshCw, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    const isChunkError =
      error?.name === "ChunkLoadError" ||
      error?.message?.includes("Loading chunk") ||
      error?.message?.includes("Failed to fetch dynamically imported module") ||
      error?.message?.includes("Unexpected token '<'");

    return { hasError: true, error, isChunkError };
  }

  componentDidCatch(error, info) {
    console.error("ErrorBoundary caught:", error, info);

    // Auto-reload on chunk load errors (stale deployment)
    if (this.state.isChunkError) {
      const reloaded = sessionStorage.getItem("chunk_reload");
      if (!reloaded) {
        sessionStorage.setItem("chunk_reload", "1");
        window.location.reload();
        return;
      }
      sessionStorage.removeItem("chunk_reload");
    }
  }

  handleRetry = () => {
    sessionStorage.removeItem("chunk_reload");
    this.setState({ hasError: false, error: null, isChunkError: false });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
          <div className="text-center max-w-md space-y-4">
            <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto" />
            <h2 className="text-xl font-bold text-white">
              {this.state.isChunkError ? "App Updated" : "Something went wrong"}
            </h2>
            <p className="text-white/40 text-sm">
              {this.state.isChunkError
                ? "A new version was deployed. Please reload to get the latest update."
                : "An unexpected error occurred while loading this page. A reload usually fixes it."}
            </p>
            <Button
              onClick={this.handleRetry}
              className="bg-purple-600 hover:bg-purple-500"
              data-testid="error-boundary-retry"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reload Page
            </Button>
            {!this.state.isChunkError && this.state.error && (
              <details className="text-left mt-4">
                <summary className="text-white/20 text-xs cursor-pointer">Technical details</summary>
                <pre className="text-white/15 text-[10px] mt-2 bg-black/30 p-2 rounded overflow-auto max-h-32">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
